import React, { useState, useEffect, useCallback, useContext, useRef, useMemo } from "react";
import { useDispatch } from "react-redux";
import { callAPI } from "../helper/apiUtils";
import { BASE_URL, KJUUP_COLORS, getAvatarInitials } from "../helper/constants";
import { $crud } from "../helper/CrudFactory";
import { $user } from "../helper/UserFactory";
import LanguageContext from "../helper/LanguageContext";
import { Oval } from "react-loader-spinner";
import { stripMarkdown, getOneToOnePartnerName, getOneToOnePartnerAvatar } from "../utils/conversationUtils";
import { isTeamsChat } from "../utils/teams/teamsChats";
import { RESET_LATEST_MESSAGES } from "../store";
import GroupAvatarImage from "./GroupAvatarImage";
import "./ForwardChatPicker.css";

const ForwardChatPicker = ({ msgIds, albums, hidden, onClose, onForwarded, onSelectMore, originGroupId }) => {
  const { strings, isGerm } = useContext(LanguageContext);
  const dispatch = useDispatch();
  const [chats, setChats] = useState([]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [peopleLoading, setPeopleLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const searchRef = useRef(null);
  const peopleTimer = useRef(null);
  const sendingRef = useRef(false);
  const openedAtRef = useRef(Date.now());
  const user = $user.get();

  useEffect(() => {
    fetchChats();
    const t = setTimeout(() => {
      if (searchRef.current) searchRef.current.focus();
    }, 100);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lock body scroll while the picker is visible (not while picking more messages)
  useEffect(() => {
    if (hidden) {
      document.body.style.overflow = "";
      return undefined;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [hidden]);

  const dismissKeepSelection = useCallback(() => {
    if (onSelectMore) onSelectMore();
    else onClose();
  }, [onSelectMore, onClose]);

  // ESC goes back to the chat without dropping the selected messages
  useEffect(() => {
    if (hidden) return undefined;
    const onKey = (e) => { if (e.key === "Escape") dismissKeepSelection(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dismissKeepSelection, hidden]);

  const fetchChats = async () => {
    try {
      const { data } = await callAPI("/group/getUserChats?page=1&limit=150", {}, "GET");
      if (data.status && data.chats) {
        // Messages cannot be forwarded into a Microsoft Teams chat (backend: forwardIn).
        setChats((data.chats || []).filter((c) => !c.isArchived && !c.group?.isArchived && !isTeamsChat(c)));
      }
    } catch (err) {
      console.error("Failed to fetch chats for forward picker:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPeople = useCallback(async (query) => {
    setPeopleLoading(true);
    try {
      const { data } = await callAPI(
        `/user/allUsersSearch?limit=40&page=1&query=${encodeURIComponent(query || "")}&forChat=1`,
        {},
        "GET"
      );
      const users = data?.data?.users || [];
      const companyUsers = (data?.data?.companyUsers || []).flatMap((cg) => cg.users || []);
      const byId = new Map();
      [...companyUsers, ...users].forEach((u) => {
        if (u?.id) byId.set(String(u.id), u);
      });
      setPeople(Array.from(byId.values()));
    } catch (err) {
      console.error("Failed to search users for forward picker:", err);
    } finally {
      setPeopleLoading(false);
    }
  }, []);

  useEffect(() => {
    if (peopleTimer.current) clearTimeout(peopleTimer.current);
    peopleTimer.current = setTimeout(() => {
      fetchPeople(search.trim());
    }, search.trim() ? 280 : 0);
    return () => {
      if (peopleTimer.current) clearTimeout(peopleTimer.current);
    };
  }, [search, fetchPeople]);

  const getDisplayName = useCallback((chat) => {
    if (chat.group?.isOneToOne) {
      const partnerName = getOneToOnePartnerName(chat, user, strings.my_notes || "My Space");
      if (partnerName) return partnerName;
      const groupName = (chat.group?.name || "").trim();
      if (groupName) return groupName;
      return (typeof chat.name === "string" ? chat.name : "").trim();
    }
    const buildingName = chat.buildingName || "";
    const rawName = (typeof chat.name === "string" ? chat.name : "") || chat.group?.name || "";
    const chatName = stripMarkdown(rawName);
    return buildingName ? `${buildingName} - ${chatName}` : chatName;
  }, [user, strings.my_notes]);

  const getPersonName = useCallback((person) => {
    if (!person) return "";
    if (person.name) return person.name;
    return `${person.firstName || ""} ${person.lastName || ""}`.trim();
  }, []);

  const getAvatarUrl = useCallback((chat) => {
    if (chat.group?.isOneToOne) {
      const path = getOneToOnePartnerAvatar(chat, user);
      if (!path) return process.env.PUBLIC_URL + "/assets/img/profile.png";
      return /^https?:\/\//i.test(path) ? path : BASE_URL + path;
    }
    if (chat.groupImage) return BASE_URL + chat.groupImage;
    return null;
  }, [user]);

  const getInitials = useCallback((name) => getAvatarInitials(name), []);

  // Exclude the origin chat and self-chats.
  const normalize = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "");

  const visibleChats = useMemo(() => {
    const myId = user?.id != null ? String(user.id) : null;
    const myNameKeys = [
      `${user?.firstName || ""}${user?.lastName || ""}`,
      user?.fullName || "",
      user?.name || "",
      user?.username || "",
      user?.email || "",
    ]
      .map(normalize)
      .filter((s) => s && s.length >= 3);

    const filtered = chats.filter((chat) => {
      const groupId = chat?.group?.id;
      if (originGroupId != null && groupId != null && String(groupId) === String(originGroupId)) {
        return false;
      }

      if (!chat?.group?.isOneToOne) return true;
      if (!myId) return true;
      if (chat.group?.isSelfChat) return false;

      if (chat.replacementUserId && String(chat.replacementUserId) === myId) {
        return false;
      }

      const groupName = (chat.group?.name || "").trim();
      if (!groupName) {
        const partnerName = getDisplayName(chat);
        if (partnerName && !myNameKeys.includes(normalize(partnerName))) {
          return true;
        }
        return false;
      }

      const groupKey = normalize(groupName);
      const fallbackKey = normalize(typeof chat.name === "string" ? chat.name : "");
      const matchesMe = myNameKeys.some(
        (k) =>
          groupKey === k ||
          groupKey.endsWith(k) ||
          fallbackKey === k ||
          fallbackKey.endsWith(k)
      );
      if (matchesMe) return false;

      const users = chat.group?.users;
      if (Array.isArray(users) && users.length > 0) {
        const allMe = users.every((u) => {
          const uId = u && typeof u === "object" ? (u.id ?? u.userId) : u;
          return uId != null && String(uId) === myId;
        });
        if (allMe) return false;
      }

      return true;
    });

    const partnerKeysFor = (chat) => {
      if (!chat?.group?.isOneToOne) return [];
      const keys = [];
      const fromUsers = Array.isArray(chat.group?.users)
        ? chat.group.users.find((u) => {
            const uId = u && typeof u === "object" ? (u.id ?? u.userId) : u;
            return uId != null && String(uId) !== String(myId);
          })
        : null;
      const partnerId =
        chat.group?.otherUserId ||
        chat.otherUserId ||
        (fromUsers && typeof fromUsers === "object" ? (fromUsers.id ?? fromUsers.userId) : fromUsers);
      if (partnerId != null) keys.push(`id:${String(partnerId)}`);
      const nameKey = normalize(getDisplayName(chat));
      if (nameKey) keys.push(`name:${nameKey}`);
      return keys;
    };

    const seenPartners = new Set();
    const seenGroupIds = new Set();
    const deduped = [];
    for (const chat of filtered) {
      const groupId = chat?.group?.id;
      if (groupId != null) {
        const gid = String(groupId);
        if (seenGroupIds.has(gid)) continue;
        seenGroupIds.add(gid);
      }
      const keys = partnerKeysFor(chat);
      if (keys.some((k) => seenPartners.has(k))) continue;
      keys.forEach((k) => seenPartners.add(k));
      deduped.push(chat);
    }
    return deduped;
  }, [chats, user?.id, user?.firstName, user?.lastName, originGroupId, getDisplayName]);

  const existingPartnerIds = useMemo(() => {
    const ids = new Set();
    visibleChats.forEach((chat) => {
      if (!chat?.group?.isOneToOne) return;
      if (chat.group.otherUserId) ids.add(String(chat.group.otherUserId));
      (chat.group.users || []).forEach((u) => {
        if (u && String(u.id || u) !== String(user?.id)) {
          ids.add(String(u.id || u));
        }
      });
    });
    return ids;
  }, [visibleChats, user?.id]);

  const existingPartnerNames = useMemo(() => {
    const names = new Set();
    visibleChats.forEach((chat) => {
      if (!chat?.group?.isOneToOne) return;
      const key = (getDisplayName(chat) || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
      if (key) names.add(key);
    });
    return names;
  }, [visibleChats, getDisplayName]);

  const filteredChats = useMemo(() => {
    if (!search.trim()) return visibleChats;
    const q = search.toLowerCase();
    return visibleChats.filter((chat) => getDisplayName(chat).toLowerCase().includes(q));
  }, [visibleChats, search, getDisplayName]);

  const visiblePeople = useMemo(() => {
    const seenIds = new Set();
    const seenNames = new Set();
    return people.filter((p) => {
      if (!p?.id || String(p.id) === String(user?.id)) return false;
      const idKey = String(p.id);
      if (seenIds.has(idKey) || existingPartnerIds.has(idKey)) return false;
      const nameKey = getPersonName(p).toLowerCase().replace(/[^a-z0-9]+/g, "");
      if (nameKey && (seenNames.has(nameKey) || existingPartnerNames.has(nameKey))) return false;
      if (originGroupId && p.groupID && String(p.groupID) === String(originGroupId)) return false;
      seenIds.add(idKey);
      if (nameKey) seenNames.add(nameKey);
      return true;
    });
  }, [people, user?.id, existingPartnerIds, existingPartnerNames, originGroupId, getPersonName]);

  const selectedCount = selectedGroups.length + selectedUserIds.length;

  const toggleGroup = useCallback((groupId) => {
    const id = String(groupId);
    setSelectedGroups((prev) =>
      prev.map(String).includes(id) ? prev.filter((g) => String(g) !== id) : [...prev, id]
    );
  }, []);

  const toggleUser = useCallback((userId) => {
    const id = String(userId);
    setSelectedUserIds((prev) =>
      prev.map(String).includes(id) ? prev.filter((u) => String(u) !== id) : [...prev, id]
    );
  }, []);

  const resolveGroupIds = useCallback(async (groupIds, userIds) => {
    const resolved = [...(groupIds || [])];
    for (const uid of userIds || []) {
      const person = people.find((p) => String(p.id) === String(uid));
      if (person?.groupID) {
        resolved.push(person.groupID);
        continue;
      }
      const { data } = await callAPI(`/group`, {}, "POST", {
        groupType: "user",
        isOneToOne: true,
        users: [user?.id, uid],
      });
      if (data?.status && data.chat?.id) {
        resolved.push(data.chat.id);
        if (person && person.isFollowing === false) {
          callAPI(`/user/${uid}/follow?id=${uid}`, {}, "GET");
        }
      }
    }
    return [...new Set(resolved.filter(Boolean).map(String))];
  }, [people, user?.id]);

  const sendForward = useCallback(async (groupIds, userIds) => {
    if (sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    try {
      const targets = await resolveGroupIds(groupIds, userIds);
      if (targets.length === 0) {
        $crud.notify({
          type: "error",
          message: strings.please_select_group_to_forward || "Please select a chat to forward to",
        });
        return;
      }
      const { data } = await callAPI("/message/forward", {}, "POST", {
        group: targets,
        id: msgIds,
        albums: Array.isArray(albums) ? albums.filter((a) => Array.isArray(a) && a.length > 1) : [],
      });
      if (data.status) {
        $crud.notify({ type: "success", message: strings.message_forwarded || "Message forwarded!" });
        dispatch({ type: RESET_LATEST_MESSAGES, status: true });
        if (onForwarded) onForwarded();
      } else {
        $crud.notify({ type: "error", message: data.message || "Failed to forward message" });
      }
    } catch (err) {
      console.error("Forward failed:", err);
      $crud.notify({ type: "error", message: "Failed to forward message" });
    } finally {
      sendingRef.current = false;
      setSending(false);
      onClose();
    }
  }, [resolveGroupIds, msgIds, albums, strings.please_select_group_to_forward, strings.message_forwarded, dispatch, onForwarded, onClose]);

  const handleForward = async () => {
    if (selectedCount === 0) {
      $crud.notify({
        type: "error",
        message: strings.please_select_group_to_forward || "Please select a chat to forward to",
      });
      return;
    }
    await sendForward(selectedGroups, selectedUserIds);
  };

  // Mobile ProForwardPicker: a tap selects the chat or person; the CTA forwards.
  const onChatRowClick = (groupId) => toggleGroup(groupId);

  const onPersonRowClick = (person) => toggleUser(person.id);

  const renderAvatar = (chat) => {
    const isOneToOne = !!chat.group?.isOneToOne;
    const avatarUrl = getAvatarUrl(chat);
    const themeColor = chat.buildingThemeColor || chat.group?.themeColor || "#737373";
    const initials = getInitials(getDisplayName(chat) || chat.name || chat.group?.name);

    if (isOneToOne) {
      return (
        <div className="fcp-avatar is-circle">
          <img
            src={avatarUrl || process.env.PUBLIC_URL + "/assets/img/profile.png"}
            alt=""
            onError={(e) => { e.target.src = process.env.PUBLIC_URL + "/assets/img/profile.png"; }}
          />
        </div>
      );
    }

    return (
      <GroupAvatarImage
        className="fcp-group-avatar"
        src={avatarUrl}
        alt=""
        initials={initials}
        themeColor={themeColor}
      />
    );
  };

  const renderPersonAvatar = (person) => {
    const src = person.img
      ? (/^https?:\/\//i.test(person.img) ? person.img : BASE_URL + person.img)
      : process.env.PUBLIC_URL + "/assets/img/profile.png";
    return (
      <div className="fcp-avatar is-circle">
        <img
          src={src}
          alt=""
          onError={(e) => { e.target.src = process.env.PUBLIC_URL + "/assets/img/profile.png"; }}
        />
      </div>
    );
  };

  const onBackdropClick = (e) => {
    // Ignore the leftover click that opened this picker, and any
    // click-through from "Select more messages" — those were aborting
    // the whole selection.
    if (Date.now() - openedAtRef.current < 400) return;
    if (e.target === e.currentTarget) dismissKeepSelection();
  };

  const hasAnyResults = filteredChats.length > 0 || visiblePeople.length > 0;
  // CTA (mobile): "Weiterleiten an 3 Chats" / "Chats auswählen".
  const ctaLabel = selectedCount > 0
    ? (selectedCount === 1
      ? (strings.chat_forward_to_one || (isGerm ? "Weiterleiten an 1 Chat" : "Forward to 1 chat"))
      : String(strings.chat_forward_to_n || (isGerm ? "Weiterleiten an {n} Chats" : "Forward to {n} chats")).replace("{n}", String(selectedCount)))
    : (strings.chat_forward_select || (isGerm ? "Chats auswählen" : "Select chats"));

  const msgCount = Array.isArray(msgIds) ? msgIds.length : 0;
  const msgLabel = msgCount === 1
    ? (strings.message || "message")
    : (strings.messages || "messages");

  return (
    <div
      className={`fcp-backdrop${hidden ? " forward-chat-picker--hidden" : ""}`}
      onMouseDown={onBackdropClick}
      aria-hidden={hidden ? "true" : undefined}
    >
      <div className="fcp-modal" role="dialog" aria-modal={!hidden}>
        <div className="fcp-header">
          <h5 className="fcp-title">
            {strings.forward_to || "Forward to..."}
          </h5>
          <div className="fcp-msg-banner">
            {msgCount} {msgLabel}
          </div>
          <button type="button" className="kj-btn kj-btn--icon kj-btn--neutral" onClick={onClose} aria-label="Close">
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <div className="fcp-search-wrap">
          <input
            ref={searchRef}
            type="text"
            className="fcp-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={strings.search_contacts || strings.search || "Search chats and people"}
            autoComplete="off"
          />
        </div>

        <div className="fcp-list">
          {loading ? (
            <div className="fcp-list-loading">
              <Oval
                height={32}
                width={32}
                color={KJUUP_COLORS.base_color}
                secondaryColor="#ddd"
                strokeWidth={4}
              />
            </div>
          ) : !hasAnyResults && !peopleLoading ? (
            <div className="fcp-list-empty">
              {strings.no_result_found || (isGerm ? "Keine Chats gefunden" : "No chats found")}
            </div>
          ) : (
            // One card: the chats, then the people the search found (mobile ProForwardPicker).
            <div className="fcp-card">
              {filteredChats.map((chat) => {
                const groupId = chat.group?.id;
                if (!groupId) return null;
                const isSelected = selectedGroups.map(String).includes(String(groupId));
                const displayName = getDisplayName(chat);
                return (
                  <div
                    key={groupId}
                    className={`fcp-row${isSelected ? " is-selected" : ""}`}
                    role="checkbox"
                    aria-checked={isSelected}
                    tabIndex={0}
                    onClick={() => onChatRowClick(groupId)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onChatRowClick(groupId);
                      }
                    }}
                  >
                    {renderAvatar(chat)}
                    <div className="fcp-name-wrap">
                      <div className="fcp-name">{displayName || "…"}</div>
                    </div>
                    <div className={`fcp-dot${isSelected ? " is-on" : ""}`} aria-hidden="true">
                      {isSelected ? <div className="material-symbols-outlined fcp-dot-icon">check</div> : null}
                    </div>
                  </div>
                );
              })}
              {peopleLoading && visiblePeople.length === 0 && search.trim() ? (
                <div className="fcp-list-loading fcp-list-loading-inline">
                  <Oval height={24} width={24} color={KJUUP_COLORS.base_color} secondaryColor="#ddd" strokeWidth={4} />
                </div>
              ) : (
                visiblePeople.map((person) => {
                  const isSelected = selectedUserIds.map(String).includes(String(person.id));
                  const displayName = getPersonName(person);
                  return (
                    <div
                      key={person.id}
                      className={`fcp-row${isSelected ? " is-selected" : ""}`}
                      role="checkbox"
                      aria-checked={isSelected}
                      tabIndex={0}
                      onClick={() => onPersonRowClick(person)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onPersonRowClick(person);
                        }
                      }}
                    >
                      {renderPersonAvatar(person)}
                      <div className="fcp-name-wrap">
                        <div className="fcp-name">{displayName}</div>
                        {!person.groupID && (
                          <div className="fcp-name-sub">
                            {strings.new_conversation || "New chat"}
                          </div>
                        )}
                      </div>
                      <div className={`fcp-dot${isSelected ? " is-on" : ""}`} aria-hidden="true">
                        {isSelected ? <div className="material-symbols-outlined fcp-dot-icon">check</div> : null}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

        <div className="fcp-footer">
          {onSelectMore && (
            <button
              type="button"
              className="kj-btn kj-btn--ghost kj-btn--sm fcp-select-more"
              onMouseDown={(e) => e.preventDefault()}
              onClick={onSelectMore}
            >
              <span className="material-symbols-outlined">checklist</span>
              {strings.select_more_messages || "Select more messages"}
            </button>
          )}
          <button
            type="button"
            className={`kj-btn kj-btn--primary kj-btn--lg kj-btn--block fcp-cta${sending ? " kj-btn--loading" : ""}`}
            onClick={handleForward}
            disabled={selectedCount === 0 && !sending}
            aria-busy={sending}
          >
            {ctaLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(ForwardChatPicker);
